/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Row, Table, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";

import {
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import { levelItem } from "./constant";
import { formatNumber } from "pages/MatchDetail/MatchedOdds";

const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  divClassName?: any;
  tableClass?: any;
  tdClass?: any;
  theadClass?: any;
  isBordered?: boolean;
  customPageSize?: number;
  isGlobalFilter?: boolean;
  isPagination: boolean;
  PaginationClassName?: string;
  SearchPlaceholder?: string;
  sorting?: boolean;
  isFilter?: boolean;
  setLimit?: any;
  setPageNo?: any;
  setFormValuesFilter?: any;
  formValuesFilter?: any;
  totalPages?: any;
  limit?: number;
  setCurrentPage?: any;
  currentPage?: any;
  totalData?: any;
  domainList?: any[];
  domainId?: string;
  showBck?: boolean;
  handleDomain?: (selectedOption: any) => void;
  tableHeight?: string;
  totals?: {
    balance: number;
    exposure_limit: number;
    credit_reference: number;
    profit_loss: number;
  };
  totalsDiamond?: any;
  handleLavelSelect?: (e: any) => void;
  handleLevel?: (e: any) => void;
  levelId?: string | number;
  handleUser?: (selectedOption: any) => void;
  userNameData?: any[];
  userName?: string;
  handleSelectUser?: any;
  setUser_name?: any;
  marketOptions?: any[];
  selectedMarketId?: any;
  handleMarketFilter?: (e: any) => void;
  handleClearMarket?: () => void;
  odds?: string;
  stack?: string;
  handleOddsStake?: (e: any, name: string) => void;
  betType?: string;
  handleBetChange?: (e: any) => void;
  columnsDateState?: {
    name: string;
    show: boolean;
  }[];
  columnsDateStateAgent?: any;
  oddsRange?: any;
  handleOddsChange?: any;
  handleRateChange?: any;
  setmatchName?: any;
  matchName?: string;
  rateRange?: {
    from: number;
    to: number;
  };
  cName?: any;
  startDate?: any;
  setStartDate?: any;
  endDate?: any;
  setEndDate?: any;
}

const TableContainer = ({
  domainId,
  setmatchName,
  matchName,
  handleDomain,
  columns,
  data,
  tableClass,
  tdClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  customPageSize,
  isGlobalFilter,
  PaginationClassName,
  SearchPlaceholder,
  sorting,
  isFilter,
  setLimit,
  setFormValuesFilter,
  formValuesFilter,
  totalPages,
  setCurrentPage,
  currentPage,
  totalData,
  domainList,
  showBck,
  tableHeight,
  handleLevel,
  levelId,
  handleUser,
  userNameData,
  userName,
  handleSelectUser,
  setUser_name,
  totals,
  totalsDiamond,
  marketOptions,
  selectedMarketId,
  handleMarketFilter,
  handleClearMarket,
  odds,
  stack,
  handleOddsStake,
  betType,
  handleBetChange,
  columnsDateState,
  columnsDateStateAgent,
  oddsRange,
  handleOddsChange,
  handleRateChange,
  rateRange,
  cName,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [isStickyActive, setStickyActive] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [showFilter, setShowFilter] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const currentDate = new Date();
  const sevenDaysAgo = new Date(
    currentDate.setDate(currentDate.getDate() - 30)
  );

  const toggleFilter = (headerId: string) => {
    if (activeFilterColumn === headerId) {
      setActiveFilterColumn(null);
      setShowFilter(false);
    } else {
      setActiveFilterColumn(headerId);
      setShowFilter(true);
    }
  };
  // console.log(data, "datadata");
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    // setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
  } = table;

  useEffect(() => {
    Number(customPageSize) && setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      if (scrollContainer) {
        const scrollLeft = scrollContainer.scrollLeft;
        setStickyActive(scrollLeft > 35);
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleInputChange = (e: any, columnId: string) => {
    const { value } = e.target;
    localStorage.setItem("report_client", value);
    setFormValuesFilter((prevValues: any) => ({
      ...prevValues,
      [columnId]: value,
    }));
  };

  useEffect(() => {
    setPageSize(customPageSize || 50);
  }, [customPageSize]);

  // const handlePageClick = (item: number) => {
  //   setCurrentPage(item + 1);
  //   setPageIndex(item);
  // };

  const handleNextPage = () => {
    nextPage();
    setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    previousPage();
    setCurrentPage(currentPage - 1);
  };

  return (
    <Fragment>
      {isGlobalFilter && (
        <DebouncedInput
          value={globalFilter ?? ""}
          onChange={(value) => setGlobalFilter(String(value))}
          className="p-2 font-lg shadow border border-block"
          placeholder={SearchPlaceholder}
        />
      )}

      <div
        className={divClassName ? divClassName : `table-responsive ${cName}`}
        ref={scrollContainerRef}
        style={{ minHeight: tableHeight }}
      >
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isIdColumn = header.column.id === "sNo";
                  const isTypeColumn = header.column.id === "is_back";
                  const isIpColumn = header.column.id === "ip";
                  const isExposure = header.column.id === "exposure";
                  const isProfitLoss = header.column.id === "profitLoss";
                  const isProfitLoss2 = header.column.id === "profitLoss2";
                  const isBalance = header.column.id === "balance";
                  const isCredit = header.column.id === "credit";
                  const isUserStatus = header.column.id === "userStatus";
                  const isBetStatus = header.column.id === "betStatus";
                  const isAction = header.column.id === "action";
                  const isDomian = header.column.id === "DomainName";
                  const isCr = header.column.id === "creditReference";
                  const isacType = header.column.id === "acType";
                  const isMobile = header.column.id === "mobile";
                  const isMobileNumber = header.column.id === "mobileNumber";
                  const isPts = header.column.id === "Pts";
                  const islevelId = header.column.id === "levelId";
                  const isUser = header.column.id === "user_name";
                  const isNotDomain = header.column.id === "sdomain";
                  const isResult = header.column.id === "result";
                  const isStatus = header.column.id === "status";
                  const isplacedTime = header.column.id === "placedTime";
                  const isBetId = header.column.id === "betID";
                  const isSelection = header.column.id === "selection";
                  const isMarket = header.column.id === "matchMarket";
                  const isRate = header.column.id === "rate";
                  const isOdds = header.column.id === "stake";
                  const isStack = header.column.id === "oddsStack";
                  const isOddsRate = header.column.id === "OddsRate";
                  const isBetType = header.column.id === "type";
                  const isPartnership = header.column.id === "partnership";
                  const isDate = header.column.id === "date";
                  const issportsSeries = header.column.id === "sportsSeries";
                  const issportsName = header.column.id === "sport_name";
                  const isSeries = header.column.id === "series_name";
                  const issportsMatch = header.column.id === "match_name";
                  const isWinner = header.column.id === "winner_name";
                  const isReason = header.column.id === "deleted_reason";
                  const isCreateAt = header.column.id === "createdAt";
                  const isBetIds = header.column.id === "betid";
                  const isBetT = header.column.id === "betType";
                  const isBetAd = header.column.id === "betAddress";
                  const issports = header.column.id === "sports";
                  const pnl = header.column.id === "pnl";
                  const ipAd = header.column.id === "ip_address";
                  const isDateFilter = header.column.id === "created";
                  const marketSelection =
                    header.column.id === "market-selection";

                  return (
                    <th key={header.id} colSpan={header.colSpan}>
                      <div className="d-flex align-items-center">
                        {header.isPlaceholder ? null : (
                          <React.Fragment>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: " ",
                              desc: " ",
                            }[header.column.getIsSorted() as string] ?? null}
                          </React.Fragment>
                        )}

                        {!isPts &&
                          !isMobile &&
                          !isMobileNumber &&
                          !isacType &&
                          !isProfitLoss2 &&
                          !isCr &&
                          !isAction &&
                          !isBetStatus &&
                          !isUserStatus &&
                          !isBalance &&
                          !isCredit &&
                          !isIdColumn &&
                          !isIpColumn &&
                          !isExposure &&
                          !isBetAd &&
                          !isProfitLoss && (
                            <i
                              {...(sorting && header.column.getCanSort()
                                ? {
                                    className: `'cursor-pointer select-none sort' ${
                                      isStickyActive
                                        ? "sticky-active"
                                        : "sticky"
                                    }`,
                                    onClick:
                                      header.column.getToggleSortingHandler(),
                                  }
                                : {})}
                              className="ms-1 ri-arrow-up-down-line text-muted fs-md align-middle cursor-pointer"
                            ></i>
                          )}

                        {isFilter && (
                          <div className="position-relative">
                            {!isPts &&
                              !isMobile &&
                              !isacType &&
                              !isProfitLoss2 &&
                              !isCr &&
                              !isAction &&
                              !isWinner &&
                              !isBetStatus &&
                              !isUserStatus &&
                              !isBalance &&
                              !isSeries &&
                              !issportsMatch &&
                              !isCredit &&
                              !isIdColumn &&
                              !isIpColumn &&
                              !isExposure &&
                              !isProfitLoss &&
                              !isNotDomain &&
                              !isResult &&
                              !isStatus &&
                              !isplacedTime &&
                              !isBetId &&
                              !isMarket &&
                              !isReason &&
                              !isCreateAt &&
                              !isBetIds &&
                              !isBetT &&
                              !isBetAd &&
                              !pnl &&
                              !issports &&
                              !isPartnership &&
                              !isDate &&
                              !issportsSeries &&
                              !issportsName && (
                                <div
                                  className="ms-1 cursor-pointer"
                                  onClick={() => toggleFilter(header.id)}
                                >
                                  <i
                                    className={`ri ri-filter-fill ${
                                      showFilter &&
                                      activeFilterColumn === header.id &&
                                      "text-primary"
                                    }`}
                                  ></i>
                                </div>
                              )}
                            {showFilter && activeFilterColumn === header.id && (
                              <div
                                ref={isDateFilter ? null : filterRef}
                                className={`${
                                  isStack || isOddsRate || isDateFilter
                                    ? ""
                                    : `${
                                        ipAd || islevelId
                                          ? "filterDropDownIp"
                                          : "filterDropDown"
                                      } bg-white shadow-lg rounded`
                                } position-absolute p-2 w-100`}
                                style={{ minWidth: "200px", zIndex: 7 }}
                              >
                                <>
                                  {isSelection || isMarket ? (
                                    <Select
                                      isClearable
                                      options={marketOptions}
                                      value={
                                        marketOptions?.find(
                                          (option: any) =>
                                            option.value === selectedMarketId
                                        ) || null
                                      }
                                      className="customSelect"
                                      onChange={(e: any) => {
                                        if (e) {
                                          handleMarketFilter &&
                                            handleMarketFilter(e.value);
                                        } else {
                                          handleClearMarket &&
                                            handleClearMarket();
                                        }
                                      }}
                                    />
                                  ) : isTypeColumn ? (
                                    <div className="ms-2">
                                      <Form.Select
                                        value={
                                          formValuesFilter[header.id] || ""
                                        }
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          handleInputChange(
                                            { target: { value } },
                                            header.id
                                          );
                                          table
                                            .getColumn(header.id)
                                            ?.setFilterValue(value);
                                        }}
                                      >
                                        <option value="">All</option>
                                        <option value={1}>Back</option>
                                        <option value={0}>Lay</option>
                                      </Form.Select>
                                    </div>
                                  ) : isUser ? (
                                    <Select
                                      isClearable
                                      options={userNameData || []}
                                      className="customSelect"
                                      value={
                                        userNameData?.find(
                                          (user: any) => user.value === userName
                                        ) || null
                                      }
                                      onInputChange={handleUser}
                                      onChange={(selectedOption: any) => {
                                        handleSelectUser(selectedOption);
                                        if (!selectedOption) {
                                          setUser_name("");
                                        }
                                      }}
                                    />
                                  ) : isDomian ? (
                                    <Select
                                      isClearable
                                      options={domainList || []}
                                      className="customSelect"
                                      value={
                                        domainList?.find(
                                          (domain: any) =>
                                            domain.value === domainId
                                        ) || null
                                      }
                                      onChange={handleDomain}
                                    />
                                  ) : islevelId ? (
                                    <div className="ms-2">
                                      <Select
                                        isClearable
                                        options={levelItem || []}
                                        className="customSelect"
                                        value={
                                          levelItem?.find(
                                            (domain: any) =>
                                              domain.value === Number(levelId)
                                          ) || null
                                        }
                                        onChange={handleLevel}
                                      />
                                    </div>
                                  ) : isRate ? (
                                    <Form.Control
                                      type="number"
                                      placeholder="Rate"
                                      value={odds}
                                      onChange={(e: any) =>
                                        handleOddsStake &&
                                        handleOddsStake(e, "odds")
                                      }
                                    />
                                  ) : isOdds ? (
                                    <Form.Control
                                      type="number"
                                      placeholder="Stack"
                                      value={stack}
                                      onChange={(e: any) =>
                                        handleOddsStake &&
                                        handleOddsStake(e, "stack")
                                      }
                                    />
                                  ) : isStack ? (
                                    <div className="dropdown custom-bottom position-absolute bg-white text-black shadow-lg p-2">
                                      <div
                                        className="position-absolute"
                                        style={{
                                          top: "-10px",
                                          left: "10px",
                                          width: "0",
                                          height: "0",
                                          borderLeft: "10px solid transparent",
                                          borderRight: "10px solid transparent",
                                          borderBottom: "10px solid #fff",
                                        }}
                                      ></div>
                                      <div className="d-flex align-items-center p-2">
                                        <div className="me-2">From</div>
                                        <input
                                          type="number"
                                          className="form-control me-2 p-2 text-center"
                                          style={{
                                            width: "80px",
                                            background:
                                              "rgb(221 221 221 / 48%)",
                                            borderRadius: "3px",
                                          }}
                                          value={oddsRange.from}
                                          onChange={(e) =>
                                            handleOddsChange(
                                              "from",
                                              e.target.value
                                            )
                                          }
                                        />
                                        <div className="me-2">To</div>
                                        <input
                                          type="number"
                                          className="form-control p-2 text-center"
                                          style={{
                                            width: "80px",
                                            background:
                                              "rgb(221 221 221 / 48%)",
                                            borderRadius: "3px",
                                          }}
                                          value={oddsRange.to}
                                          onChange={(e) =>
                                            handleOddsChange(
                                              "to",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : isBetType ? (
                                    <Form.Select
                                      id="floatingSelect"
                                      aria-label="Floating label select example"
                                      value={betType}
                                      onChange={handleBetChange}
                                    >
                                      <option selected>Choose...</option>
                                      <option value="">All</option>
                                      <option value="1">Back</option>
                                      <option value="0">Lay</option>
                                    </Form.Select>
                                  ) : isOddsRate ? (
                                    <div className="dropdown custom-bottom position-absolute bg-white text-black shadow-lg p-2">
                                      <div
                                        className="position-absolute"
                                        style={{
                                          top: "-10px",
                                          left: "10px",
                                          width: "0",
                                          height: "0",
                                          borderLeft: "10px solid transparent",
                                          borderRight: "10px solid transparent",
                                          borderBottom: "10px solid #fff",
                                        }}
                                      ></div>
                                      <div className="d-flex align-items-center p-2">
                                        <div className="me-2">From</div>
                                        <input
                                          type="number"
                                          className="form-control me-2 p-2 text-center"
                                          style={{
                                            width: "80px",
                                            background:
                                              "rgb(221 221 221 / 48%)",
                                            borderRadius: "3px",
                                          }}
                                          value={rateRange?.from}
                                          onChange={(e) =>
                                            handleRateChange(
                                              "from",
                                              e.target.value
                                            )
                                          }
                                        />
                                        <div className="me-2">To</div>
                                        <input
                                          type="number"
                                          className="form-control p-2 text-center"
                                          style={{
                                            width: "80px",
                                            background:
                                              "rgb(221 221 221 / 48%)",
                                            borderRadius: "3px",
                                          }}
                                          value={rateRange?.to}
                                          onChange={(e) =>
                                            handleRateChange(
                                              "to",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : isDateFilter ? (
                                    <div
                                      style={{
                                        minWidth: "200px",
                                      }}
                                      className="dropdown custom-bottom position-absolute bg-white text-black shadow-lg p-2"
                                    >
                                      <div
                                        className="position-absolute"
                                        style={{
                                          top: "-10px",
                                          left: "10px",
                                          width: "0",
                                          height: "0",
                                          borderLeft: "10px solid transparent",
                                          borderRight: "10px solid transparent",
                                          borderBottom: "10px solid #fff",
                                        }}
                                      ></div>
                                      <div className="align-items-center p-2">
                                        <div className="mb-3">
                                          <div className="me-2 mb-1">
                                            Start Date
                                          </div>
                                          <Flatpickr
                                            className="form-control"
                                            pl
                                            options={{
                                              enableTime: true,
                                              dateFormat: "Y-m-d H:i:s",
                                              defaultDate: [startDate],
                                              maxDate: endDate,
                                            }}
                                            value={startDate || sevenDaysAgo}
                                            onChange={([date]: [Date]) => {
                                              setStartDate(new Date(date));
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <div className="me-2 mb-1">
                                            End Date
                                          </div>
                                          <Flatpickr
                                            className="form-control"
                                            pl
                                            options={{
                                              enableTime: true,
                                              dateFormat: "Y-m-d H:i:s",
                                              defaultDate: [endDate],
                                              maxDate: endDate,
                                            }}
                                            value={endDate || new Date()}
                                            onChange={([date]: [Date]) => {
                                              setEndDate(new Date(date));
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : marketSelection ? (
                                    <div className="ms-2">
                                      <Select
                                        isClearable
                                        options={
                                          data
                                            ?.map((item: any) => {
                                              const label =
                                                item?.is_fancy === 0
                                                  ? item?.selection_name
                                                  : item?.market_name;
                                              return { label, value: label };
                                            })
                                            .filter(
                                              (
                                                value: any,
                                                index: any,
                                                self: any
                                              ) =>
                                                index ===
                                                self.findIndex(
                                                  (v: any) =>
                                                    v.value === value.value
                                                )
                                            ) || []
                                        }
                                        value={
                                          data
                                            ?.map((item: any) => {
                                              const label =
                                                item?.is_fancy === 0
                                                  ? item?.selection_name
                                                  : item?.market_name;
                                              return { label, value: label };
                                            })
                                            .find(
                                              (domain: any) =>
                                                domain.value === matchName
                                            ) || null
                                        }
                                        onChange={(selectedOption: any) => {
                                          setmatchName(
                                            selectedOption?.value || ""
                                          );
                                        }}
                                        styles={{
                                          // container: (base:any) => ({
                                          //   ...base,
                                          //   display: "inline-block",
                                          //   width: "auto",
                                          //   minWidth: "100px", // fallback for short labels
                                          // }),
                                          // control: (base:any) => ({
                                          //   ...base,
                                          //   width: "auto",
                                          //   minWidth: "100px",
                                          // }),
                                          menu: (base: any) => ({
                                            ...base,
                                            width: "max-content",
                                            minWidth: "100%",
                                          }),
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <Form.Control
                                      type="text"
                                      placeholder="Search..."
                                      name="user_name"
                                      value={formValuesFilter[header.id] || ""}
                                      onChange={(e) =>
                                        handleInputChange(e, header.id)
                                      }
                                    />
                                  )}
                                </>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => {
              const is_back = row?.original?.is_back === 1;

              return (
                <tr
                  key={row.id}
                  className={showBck ? (is_back ? "backRow" : "layRow") : ""}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        className={`${tdClass} ${
                          isStickyActive ? "sticky-active" : "sticky"
                        }`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {totals && data?.length !== 0 && (
              <tr style={{ position: "relative", zIndex: 1 }}>
                <td className="fs-6 fw-bolder text-center">Total</td>
                {columnsDateState?.map((column, index) => {
                  if (column?.name === "User Name") return null;
                  if (column.show) {
                    let value;
                    let isNegative = false;
                    switch (column.name) {
                      case "C R":
                        value = totals?.credit_reference;
                        isNegative = value < 0;
                        break;
                      case "Exposure":
                        value = totals?.exposure_limit;
                        isNegative = value < 0;
                        break;
                      case "P|L":
                        value = totals?.profit_loss;
                        isNegative = value < 0;
                        break;
                      case "Balance":
                        value = totals?.balance;
                        isNegative = value < 0;
                        break;
                      default:
                        value = null;
                    }
                    return (
                      <td
                        key={index}
                        className={`fw-bolder ${
                          isNegative ? "text-danger" : "text-success"
                        }`}
                      >
                        {value?.toFixed(2)}
                      </td>
                    );
                  }
                  return null;
                })}
              </tr>
            )}
            {totalsDiamond && data?.length !== 0 && (
              <>
                <tr style={{ position: "relative", zIndex: 1 }}>
                  <td className="fs-6 fw-bolder text-center">Total</td>
                  {columnsDateStateAgent?.map((column: any, index: number) => {
                    if (
                      column?.name === "User Name" ||
                      column?.name === "Partnership"
                    )
                      return null;
                    if (column.show) {
                      let value;
                      let isNegative = false;

                      switch (column.name) {
                        case "C R":
                          value = formatNumber(
                            totalsDiamond?.credit_reference?.toFixed(2)
                          );
                          isNegative = totalsDiamond?.credit_reference < 0;
                          break;
                        case "Exposure":
                          value = formatNumber(
                            totalsDiamond?.exposure_limit?.toFixed(2)
                          );
                          isNegative = totalsDiamond?.exposure_limit < 0;
                          break;
                        case "Client P|L":
                          const plValue =
                            totalsDiamond?.profit_loss?.toFixed(2);
                          isNegative = parseFloat(plValue) < 0;
                          value = formatNumber(plValue);
                          break;
                        case "Client P|L %":
                          const plPercentValue =
                            totalsDiamond?.client_pl_share?.toFixed(2);
                          isNegative = parseFloat(plPercentValue) < 0;
                          value = formatNumber(plPercentValue);
                          break;
                        case "Avail. Balance":
                          value = formatNumber(
                            totalsDiamond?.balance?.toFixed(2)
                          );
                          isNegative = totalsDiamond?.balance < 0;
                          break;
                        default:
                          value = null;
                      }

                      return (
                        <td
                          key={index}
                          className={`fw-bolder ${
                            isNegative ? "text-danger" : "text-success"
                          }`}
                        >
                          {value}
                        </td>
                      );
                    }
                    return null;
                  })}
                </tr>
              </>
            )}
          </tbody>
        </Table>
        {data?.length === 0 && (
          <div className="noData text-center p-3 fs-md text-muted">
            No Data Found!
          </div>
        )}
      </div>

      {isPagination && (
        <Row
          className={`${PaginationClassName} flex-wrap justify-content-center`}
        >
          <div className="col-sm mt-3">
            <div className="text-muted d-flex align-items-center">
              Showing
              <select
                className="mx-2 border"
                onChange={(e: any) => setLimit(e.target.value)}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              of{" "}
              <span className="fw-semibold ms-1">
                {totalData || data?.length}
              </span>{" "}
              <span className="ms-1">Results</span>
            </div>
          </div>
          <div className="col-sm-auto mt-3 mt-sm-0">
            <div className="pagination-wrap hstack gap-2 justify-content-center">
              <Button
                variant="link"
                className="page-item pagination-prev"
                disabled={currentPage === 1 || !currentPage}
                onClick={handlePreviousPage}
              >
                <i className="mdi mdi-chevron-left align-middle"></i>
              </Button>
              <ul className="pagination listjs-pagination mb-0">
                <li className={"active"}>
                  <Link to="#" className="page">
                    {currentPage || 1}
                  </Link>
                </li>
              </ul>
              <Button
                variant="link"
                className="page-item pagination-next"
                disabled={totalPages === currentPage}
                onClick={handleNextPage}
              >
                <i className="mdi mdi-chevron-right align-middle"></i>
              </Button>
            </div>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
